/**
 * Open Initiatives Tab — lists publicly joinable initiatives
 * @module components/initiatives/open-initiatives-tab
 */

import { Inbox } from 'lucide-react';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { Initiative } from '@/types/initiative';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface OpenInitiativesTabProps {
  initiatives: Initiative[];
  loading: boolean;
  error: string | null;
  locale: Locale;
  onJoin: (initiative: Initiative) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
    case 'published':
      return 'text-success-dark dark:text-success bg-success-light dark:bg-success/20';
    case 'draft':
    case 'funded':
      return 'text-warning-dark dark:text-warning bg-warning-light dark:bg-warning/20';
    case 'paused':
      return 'text-warning-dark dark:text-warning bg-warning-light dark:bg-warning/20';
    case 'completed':
    case 'expired':
      return 'text-info-dark dark:text-info bg-info-light dark:bg-info/20';
    case 'cancelled':
      return 'text-error-dark dark:text-error bg-error-light dark:bg-error/20';
    default:
      return 'text-foreground-secondary bg-muted';
  }
};

const getLocalizedTitle = (initiative: Initiative, locale: Locale): string => {
  if (locale === 'ar' && initiative.title_ar) return initiative.title_ar;
  if (locale === 'en' && initiative.title_en) return initiative.title_en;
  return initiative.title || initiative.title_en || initiative.title_ar || '-';
};

export function OpenInitiativesTab({
  initiatives,
  loading,
  error,
  locale,
  onJoin,
}: OpenInitiativesTabProps) {

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-8">
        <LoadingSpinner size="lg" label={t(locale, 'loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-8">
        <p className="text-sm text-red-500 text-center">
          {error}
        </p>
      </div>
    );
  }

  if (initiatives.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-8">
        <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
          <div className="rounded-full bg-muted p-4">
            <Inbox className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          </div>
          <p className="text-sm text-foreground-secondary">
            {t(locale, 'noOpenInitiatives')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table
          className="w-full"
          role="table"
          aria-label={t(locale, 'openInitiativesTab')}
        >
          <thead className="bg-muted/50 text-foreground-secondary text-sm font-medium">
            <tr>
              <th scope="col" className="px-6 py-4 text-start">
                {t(locale, 'title')}
              </th>
              <th scope="col" className="px-6 py-4 text-start">
                {t(locale, 'type')}
              </th>
              <th scope="col" className="px-6 py-4 text-start">
                {t(locale, 'status')}
              </th>
              <th scope="col" className="px-6 py-4">
                <span className="sr-only">{t(locale, 'actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {initiatives.map((initiative) => (
              <tr
                key={initiative.id}
                className="hover:bg-muted/50 transition-colors"
              >
                <td className="px-6 py-4 text-sm font-medium max-w-xs">
                  <span className="text-foreground truncate block">
                    {getLocalizedTitle(initiative, locale)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-foreground-secondary">
                  {initiative.type}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(initiative.status)}`}
                  >
                    {t(locale, initiative.status as Parameters<typeof t>[1]) || initiative.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-end">
                  <button
                    type="button"
                    onClick={() => onJoin(initiative)}
                    className="inline-flex items-center gap-1.5 bg-primary hover:bg-amber-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-sm shadow-primary/20"
                  >
                    {t(locale, 'joinInitiative')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
